import {
  Army,
  ArmyList,
  ArmyOrganisationGroup,
  ConstrainedItem,
  ConstrainedObject,
  Constraint,
  ConstraintField,
  ConstraintItemType,
  ConstraintScope,
  ConstraintType,
  Unit,
} from '@Models';

export const armyOrganisationGroupConstraintableElements = (
  group: ArmyOrganisationGroup,
  armyId: number,
  armyListMaxPoints: number,
): ConstrainedObject[] => {
  return [
    {
      parent: {
        id: armyId,
        type: ConstraintItemType.Army,
        name: 'Army',
      } satisfies ConstrainedItem as ConstrainedItem,

      type: ConstraintItemType.Organisation,
      id: group.organisationId,
      name: group.name,

      hidden: group.hidden,
      constraints: group.constraints.map(
        (constraint) =>
          ({
            ...constraint,
            value: constraint.percentValue
              ? Math.round((armyListMaxPoints * constraint.value) / 100)
              : constraint.value,
          }) satisfies Constraint as Constraint,
      ),
      modifiers: group.modifiers,
    } satisfies ConstrainedObject as ConstrainedObject,
  ];
};

export const unitConstraintableElements = (
  unit: Unit,
  armyId: number,
  armyOrganisationId: number | null,
): ConstrainedObject[] => {
  return [
    {
      parent: {
        id: armyId,
        type: ConstraintItemType.Army,
        name: 'Army',
      } satisfies ConstrainedItem as ConstrainedItem,

      type: ConstraintItemType.Unit,
      id: unit.id,
      name: unit.name,

      hidden: unit.hidden,
      constraints: unit.constraints,
      modifiers: unit.modifiers,
    } satisfies ConstrainedObject as ConstrainedObject,
    ...unit.optionGroups.flatMap((group) => [
      {
        parent: {
          id: unit.id,
          type: ConstraintItemType.Unit,
          name: unit.name,
        } satisfies ConstrainedItem as ConstrainedItem,

        type: ConstraintItemType.UnitOptionGroup,
        id: group.id,
        name: group.name,

        hidden: group.hidden,
        constraints: group.constraints,
        modifiers: group.modifiers,
      } satisfies ConstrainedObject as ConstrainedObject,
      ...group.options.map(
        (option) =>
          ({
            parent: {
              type: ConstraintItemType.UnitOptionGroup,
              id: group.id,
              name: group.name,
            } satisfies ConstrainedItem as ConstrainedItem,

            type: ConstraintItemType.UnitOption,
            id: option.id,
            name: option.name,

            hidden: option.hidden,
            constraints: option.constraints,
            modifiers: option.modifiers,
          }) satisfies ConstrainedObject as ConstrainedObject,
      ),
    ]),
    ...unit.troops.flatMap(
      (troop) =>
        ({
          parent: {
            id: unit.id,
            type: ConstraintItemType.Unit,
            name: unit.name,
          } satisfies ConstrainedItem as ConstrainedItem,

          type: ConstraintItemType.Troop,
          id: troop.id,
          name: troop.name,

          hidden: troop.hidden,
          constraints: troop.constraints,
          modifiers: troop.modifiers,
        }) satisfies ConstrainedObject as ConstrainedObject,
    ),
  ].map((item) => {
    return {
      ...item,
      constraints: item.constraints.filter(
        (constraint) =>
          !constraint.armyOrganisationId ||
          constraint.armyOrganisationId === armyOrganisationId,
      ),
    };
  });
};

export const allConstraintableElement = (
  army: Army | null,
  armyList: ArmyList | null,
): ConstrainedObject[] => {
  const armyId = army?.id || 0;
  const units = army?.units || [];

  const max = armyList?.max || 0;

  const armyOrganisationGroups =
    (army?.army_organisations ?? []).find(
      (org) => armyList?.army_organisation_id === org.id,
    )?.army_organisation_groups ?? [];

  return [
    {
      parent: {
        id: armyId,
        type: ConstraintItemType.Army,
        name: 'Army',
      } satisfies ConstrainedItem as ConstrainedItem,

      type: ConstraintItemType.Roster,
      id: 0,
      name: 'ArmyList',

      hidden: false,
      constraints: [
        {
          id: '-',
          versionId: 0,
          armyId: 0,
          armyOrganisationId: null,
          name: 'ArmyList Max',
          includeChildSelections: true,
          scope: ConstraintScope.Roster,
          shared: true,
          type: ConstraintType.Max,
          value: max,
          percentValue: false,
          field: ConstraintField.Points,
        } satisfies Constraint as Constraint,
      ],
      modifiers: [],
    } satisfies ConstrainedObject as ConstrainedObject,
    ...armyOrganisationGroups.flatMap((org) =>
      armyOrganisationGroupConstraintableElements(
        org,
        armyId,
        armyList?.max ?? 0,
      ),
    ),
    ...units.flatMap((unit) =>
      unitConstraintableElements(
        unit,
        armyId,
        armyList?.army_organisation_id ?? null,
      ),
    ),
  ];
};
